import { useState, useEffect } from "react";
import WidgetContainer from "../reusablesComponents/containerWidget";
import GroupSuperbowl from "./groupSuperbowl/groupSuperbowl";
import { getPlayoffTeams, generateBracket, simulateGame } from "../../services/superBowlService";
import ButtonsSuperbowl from "./buttonsbowl/buttonsbowl";
import LoadingOverlay from "../simulatorTables/loadingSimulator/loadingOverlay";
import "./simulatorSuperBowl.sass";
import ErrorMessage from "../simulatorMundial/errorMessage/ErrorMessage";

export default function SimulatorSuperBowl() {
  const [loading, setLoading] = useState(false);
  const [bracket, setBracket] = useState(null);
  const [teams, setTeams] = useState(null);
  const [superBowlWinner, setSuperBowlWinner] = useState(null);
  const [superBowlGame, setSuperBowlGame] = useState();
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeSimulator();
  }, []);

  useEffect(() => {
    // Reiniciar el ganador del Super Bowl cuando el bracket cambia
    setSuperBowlWinner(null);
  }, [bracket]);

  const initializeSimulator = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener equipos
      const teamsData = await getPlayoffTeams();
      setTeams(teamsData);

      // Generar bracket inicial
      let bracketData = await generateBracket();
      // Preseleccionar equipos seed 1 en ronda divisional
      bracketData = preSelectSeed1Teams(bracketData, teamsData);

      setBracket(bracketData);
      setSuperBowlWinner(null);

      // Crear el juego del Super Bowl con los ganadores de las conferencias
      const afcChampion = bracketData.afc?.conferenceChampionship?.game?.winner;
      const nfcChampion = bracketData.nfc?.conferenceChampionship?.game?.winner;

      if (afcChampion && nfcChampion) {
        const superBowlGameData = {
          id: "SUPER_BOWL",
          homeTeam: afcChampion,
          awayTeam: nfcChampion,
          round: "superbowl",
          status: "pending"
        };
        setSuperBowlGame(superBowlGameData);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error initializing simulator:", err);
      setError("Error al inicializar el simulador: " + err.message);
      setLoading(false);
    }
  };

  const preSelectSeed1Teams = (bracketData, teamsData) => {
    // Encontrar equipos seed 1
    const afcSeed1 = teamsData.afc.find(t => t.seed === 1);
    const nfcSeed1 = teamsData.nfc.find(t => t.seed === 1);

    // Mapeo de ganadores de comodín
    const afcWildcardWinners = {
      buf: teamsData.afc.find(t => t.shortName === "BUF"),
      hou: teamsData.afc.find(t => t.shortName === "HOU"),
      ne: teamsData.afc.find(t => t.shortName === "NE")
    };

    const nfcWildcardWinners = {
      sf: teamsData.nfc.find(t => t.shortName === "SF"),
      la: teamsData.nfc.find(t => t.shortName === "LA"),
      chi: teamsData.nfc.find(t => t.shortName === "CHI")
    };

    // Equipos ganadores por defecto para Conference Championship
    const nfcConferenceWinners = {
      la: teamsData.nfc.find(t => t.shortName === "LA"),  // Los Angeles Rams
      sea: teamsData.nfc.find(t => t.shortName === "SEA") //
    };

    const afcConferenceWinners = {
      den: teamsData.afc.find(t => t.shortName === "DEN"),  //
      ne: teamsData.afc.find(t => t.shortName === "NE") // New England Patriots
    };

    // Equipos ganadores por por defecto para la final
    const nfcFinalWinners = {
      sea: teamsData.nfc.find(t => t.shortName === "SEA") //
    };

    const afcFinalWinners = {
      ne: teamsData.afc.find(t => t.shortName === "NE") // New England Patriots
    };



    // Configurar equipos en ronda comodín (wildcard) sin preseleccionar ganadores
    if (bracketData.afc.wildCardRound && bracketData.afc.wildCardRound.games) {
      bracketData.afc.wildCardRound.games.forEach((game, idx) => {
        if (idx === 0 && afcWildcardWinners.buf) {
          // Solo configurar equipos, no ganadores
          if (!game.homeTeam) game.homeTeam = afcWildcardWinners.buf;
          if (!game.awayTeam) game.awayTeam = afcWildcardWinners.buf;
        } else if (idx === 1 && afcWildcardWinners.hou) {
          if (!game.homeTeam) game.homeTeam = afcWildcardWinners.hou;
          if (!game.awayTeam) game.awayTeam = afcWildcardWinners.hou;
        }
      });
    }

    if (bracketData.nfc.wildCardRound && bracketData.nfc.wildCardRound.games) {
      bracketData.nfc.wildCardRound.games.forEach((game, idx) => {
        if (idx === 0 && nfcWildcardWinners.sf) {
          // Solo configurar equipos, no ganadores
          if (!game.homeTeam) game.homeTeam = nfcWildcardWinners.sf;
          if (!game.awayTeam) game.awayTeam = nfcWildcardWinners.sf;
        } else if (idx === 1 && nfcWildcardWinners.la) {
          if (!game.homeTeam) game.homeTeam = nfcWildcardWinners.la;
          if (!game.awayTeam) game.awayTeam = nfcWildcardWinners.la;
        }
      });
    }

    // Preseleccionar seed 1 como homeTeam en divisional
    if (afcSeed1 && bracketData.afc.divisionalRound.games[0]) {
      bracketData.afc.divisionalRound.games[0].homeTeam = afcSeed1;
    }

    if (afcWildcardWinners.ne && bracketData.afc.divisionalRound.games[1]) {
      bracketData.afc.divisionalRound.games[1].homeTeam = afcWildcardWinners.ne;
    }

    if (nfcSeed1 && bracketData.nfc.divisionalRound.games[0]) {
      bracketData.nfc.divisionalRound.games[0].homeTeam = nfcSeed1;
    }

    if (nfcWildcardWinners.chi && bracketData.nfc.divisionalRound.games[1]) {
      bracketData.nfc.divisionalRound.games[1].homeTeam = nfcWildcardWinners.chi;
    }

    // Agregar ganadores de comodín como awayTeam en divisional
    if (afcWildcardWinners.buf && bracketData.afc.divisionalRound.games[0]) {
      bracketData.afc.divisionalRound.games[0].awayTeam = afcWildcardWinners.buf;
    }

    if (afcWildcardWinners.hou && bracketData.afc.divisionalRound.games[1]) {
      bracketData.afc.divisionalRound.games[1].awayTeam = afcWildcardWinners.hou;
    }

    if (nfcWildcardWinners.sf && bracketData.nfc.divisionalRound.games[0]) {
      bracketData.nfc.divisionalRound.games[0].awayTeam = nfcWildcardWinners.sf;
    }

    if (nfcWildcardWinners.la && bracketData.nfc.divisionalRound.games[1]) {
      bracketData.nfc.divisionalRound.games[1].awayTeam = nfcWildcardWinners.la;
    }

    // Preseleccionar ganadores de la ronda divisional
    // AFC Divisional Winners
    if (bracketData.afc.divisionalRound.games[0] && afcConferenceWinners.ne) {
      bracketData.afc.divisionalRound.games[0].winner = afcConferenceWinners.ne;
    }

    if (bracketData.afc.divisionalRound.games[1] && afcConferenceWinners.den) {
      bracketData.afc.divisionalRound.games[1].winner = afcConferenceWinners.den;
    }

    // NFC Divisional Winners
    if (bracketData.nfc.divisionalRound.games[0] && nfcConferenceWinners.sea) {
      bracketData.nfc.divisionalRound.games[0].winner = nfcConferenceWinners.sea;
    }

    if (bracketData.nfc.divisionalRound.games[1] && nfcConferenceWinners.la) {
      bracketData.nfc.divisionalRound.games[1].winner = nfcConferenceWinners.la;
    }

    // Configurar equipos y ganadores en Conference Championship
    // AFC Conference Championship
    if (bracketData.afc.conferenceChampionship && bracketData.afc.conferenceChampionship.game) {
      if (afcConferenceWinners.ne && afcConferenceWinners.den) {
        if (!bracketData.afc.conferenceChampionship.game.homeTeam) {
          bracketData.afc.conferenceChampionship.game.homeTeam = afcConferenceWinners.ne;
        }
        if (!bracketData.afc.conferenceChampionship.game.awayTeam) {
          bracketData.afc.conferenceChampionship.game.awayTeam = afcConferenceWinners.den;
        }
        // Preseleccionar ganador AFC
        if (afcFinalWinners.ne) {
          bracketData.afc.conferenceChampionship.game.winner = afcFinalWinners.ne;
        }
      }
    }

    // NFC Conference Championship  
    if (bracketData.nfc.conferenceChampionship && bracketData.nfc.conferenceChampionship.game) {
      if (nfcConferenceWinners.sea && nfcConferenceWinners.la) {
        if (!bracketData.nfc.conferenceChampionship.game.homeTeam) {
          bracketData.nfc.conferenceChampionship.game.homeTeam = nfcConferenceWinners.sea;
        }
        if (!bracketData.nfc.conferenceChampionship.game.awayTeam) {
          bracketData.nfc.conferenceChampionship.game.awayTeam = nfcConferenceWinners.la;
        }
        // Preseleccionar ganador NFC
        if (nfcFinalWinners.sea) {
          bracketData.nfc.conferenceChampionship.game.winner = nfcFinalWinners.sea;
        }
      }
    }

    return bracketData;
  };

  const handleGameSimulation = async (gameId, winnerTeam) => {
    try {
      setLoading(true);
      setError(null);

      // Special handling for Super Bowl - no API call, just update local state
      if (gameId === "SUPER_BOWL") {
        setSuperBowlWinner(winnerTeam);
        setLoading(false);
        return;
      }

      // Simular el juego - pasar el objeto del equipo completo
      const response = await simulateGame(gameId, winnerTeam.id, bracket);

      // La respuesta del API contiene el bracket actualizado
      const updatedBracket = response.bracket || response;

      setBracket(updatedBracket);

      // Verificar si hay ganadores de conferencia para crear o actualizar el Super Bowl
      const afcChampion = updatedBracket.afc?.conferenceChampionship?.game?.winner;
      const nfcChampion = updatedBracket.nfc?.conferenceChampionship?.game?.winner;

      // Crear o actualizar el juego del Super Bowl con los campeones disponibles
      if (afcChampion || nfcChampion) {
        const superBowlGameData = {
          id: "SUPER_BOWL",
          homeTeam: afcChampion || null,
          awayTeam: nfcChampion || null,
          round: "superbowl",
          status: "pending"
        };
        setSuperBowlGame(superBowlGameData);
      }

      // Verificar si hay Super Bowl en la respuesta
      if (updatedBracket.superBowl && updatedBracket.superBowl.game) {
        setSuperBowlGame(updatedBracket.superBowl.game);

        // Si el Super Bowl ya tiene ganador
        if (updatedBracket.superBowl.game.winner) {
          setSuperBowlWinner(updatedBracket.superBowl.game.winner);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Error simulating game:", err);
      setError("Error al simular el juego: " + err.message);
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      setError(null);

      // Pequeño delay para que se vea la transición
      await new Promise(resolve => setTimeout(resolve, 480));

      // Reinicializar
      const teamsData = await getPlayoffTeams();
      let bracketData = await generateBracket();
      bracketData = preSelectSeed1Teams(bracketData, teamsData);

      setBracket(bracketData);
      setSuperBowlWinner(null);

      // Crear el juego del Super Bowl con los ganadores de las conferencias
      const afcChampion = bracketData.afc?.conferenceChampionship?.game?.winner;
      const nfcChampion = bracketData.nfc?.conferenceChampionship?.game?.winner;

      if (afcChampion && nfcChampion) {
        const superBowlGameData = {
          id: "SUPER_BOWL",
          homeTeam: afcChampion,
          awayTeam: nfcChampion,
          round: "superbowl",
          status: "pending"
        };
        setSuperBowlGame(superBowlGameData);
      }

      // Otro pequeño delay antes de quitar el loading
      await new Promise(resolve => setTimeout(resolve, 200));
      setLoading(false);
    } catch (err) {
      setError("Error al reiniciar el simulador: " + err.message);
      setLoading(false);
    }
  };

  if (error) {
    return (
      <WidgetContainer loading={loading} className="container__simulador-superbowl">
        <ErrorMessage message={"error"} />

        <button className="reset-superbowl-button reinit-superbowl-button" onClick={initializeSimulator}>
          Reintentar
        </button>
      </WidgetContainer>
    );
  }


  return (
    <>
      {(!bracket || !teams)
        ?
        <LoadingOverlay show={(!bracket || !teams) ? true : false} backgroundColor="rgb(255 255 255 / 50%)" />
        :
        <WidgetContainer loading={(!bracket || !teams) ? true : false} className="container__simulador-superbowl">
          <div className="conferences-container">
            <div className="conference-section section-afc">
              {bracket.afc && (
                <GroupSuperbowl
                  bracket={bracket.afc}
                  conference="AFC"
                  teams={teams.afc}
                  onGameSimulate={handleGameSimulation}
                />
              )}
            </div>

            <div className="superbowl-section">
              <ButtonsSuperbowl {...{ handleReset, superBowlWinner }} />

              <GroupSuperbowl
                bracket={{ conferenceChampionship: { game: superBowlGame } }}
                conference="SUPER_BOWL"
                teams={[...teams.afc, ...teams.nfc]}
                onGameSimulate={handleGameSimulation}
                winnerTeam={superBowlWinner}
              />
            </div>

            <div className="conference-section section-nfc">
              {bracket.nfc && (
                <GroupSuperbowl
                  bracket={bracket.nfc}
                  conference="NFC"
                  teams={teams.nfc}
                  onGameSimulate={handleGameSimulation}
                />
              )}
            </div>
          </div>
        </WidgetContainer>
      }
    </>
  );
}